import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { logger } from "@/lib/logger";
import {
  ActivityAgentProcessingStatusDto,
  ActivityDto,
  ActivityWithAuditDto,
  ActivityAutofill,
  AccountActivitySparklineDto,
  PastMeetingActivityDto,
  UpcomingMeetingActivityDto,
} from "@/types";
import { CreateActivityInput, UpdateActivityInput } from "@/schemas";

export type CreateActivityRequest = CreateActivityInput;
export type UpdateActivityRequest = UpdateActivityInput;
export type ActivityAutofillRequest = {
  content: string;
  accountName: string;
};

/**
 * Creates a new activity by sending the provided data to the backend.
 *
 * @param accountId - The ID of the account to associate the activity with.
 * @param data - The data required to create a new activity.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createActivity(
  data: CreateActivityRequest
): Promise<ActivityDto> {
  try {
    const accountId = data.accountId;
    return await apiClient<ActivityDto>(
      `/api/accounts/${accountId}/activities`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "createActivity");
    throw error;
  }
}

/**
 * Updates an existing activity by sending the provided data to the backend.
 *
 * @param activityId - The ID of the activity to update
 * @param data - The data required to update the activity.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateActivity(
  activityId: string,
  data: UpdateActivityRequest
): Promise<ActivityWithAuditDto> {
  try {
    return await apiClient<ActivityWithAuditDto>(
      `/api/activities/${activityId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "updateActivity");
    throw error;
  }
}

/**
 * Fetches all activities for one account.
 *
 * @param accountId - The ID of the account to fetch activities for
 * @returns A list of activities.
 * @throws An error if the request fails.
 */
export async function fetchActivitiesByAccountId(
  accountId: string
): Promise<ActivityWithAuditDto[]> {
  try {
    return await apiClient<ActivityWithAuditDto[]>(
      `/api/accounts/${accountId}/activities`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchActivitiesByAccountId");
    throw error;
  }
}

/**
 * Fetches activity-level AI processing status.
 *
 * @param activityId - The ID of the activity to fetch agent status for.
 * @returns Current agent status for each generated object type.
 */
export async function fetchActivityAgentProcessingStatus(
  activityId: string
): Promise<ActivityAgentProcessingStatusDto> {
  try {
    return await apiClient<ActivityAgentProcessingStatusDto>(
      `/api/activities/${activityId}/agent-processing-status`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchActivityAgentProcessingStatus");
    throw error;
  }
}

/**
 * Fetches upcoming activities for an account (status = UPCOMING).
 * Used by the "Upcoming Events" widget on the account brief page.
 *
 * @param accountId - The ID of the account to fetch upcoming activities for
 * @returns A list of upcoming activities, ordered by soonest first.
 * @throws An error if the request fails.
 */
export async function fetchUpcomingActivitiesByAccountId(
  accountId: string
): Promise<UpcomingMeetingActivityDto[]> {
  try {
    return await apiClient<UpcomingMeetingActivityDto[]>(
      `/api/accounts/${accountId}/activities?upcoming=true`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchUpcomingActivitiesByAccountId");
    throw error;
  }
}

/**
 * Fetches the account's past calendar meetings (most recent first, up to 3).
 * Used by the "Past Meetings" widget on the account brief page.
 *
 * @param accountId - The ID of the account to fetch past meetings for
 * @returns A list of past meetings with their update counts.
 * @throws An error if the request fails.
 */
export async function fetchPastActivitiesByAccountId(
  accountId: string
): Promise<PastMeetingActivityDto[]> {
  try {
    return await apiClient<PastMeetingActivityDto[]>(
      `/api/accounts/${accountId}/activities?past=true`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchPastActivitiesByAccountId");
    throw error;
  }
}

/**
 * Fetches all activities for one contact.
 *
 * @param contactId - The ID of the contact to fetch activities for
 * @returns A list of activities.
 * @throws An error if the request fails.
 */
export async function fetchActivitiesByContactId(
  contactId: string
): Promise<ActivityDto[]> {
  try {
    return await apiClient<ActivityDto[]>(
      `/api/contacts/${contactId}/activities`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchActivitiesByContactId");
    throw error;
  }
}

export async function fetchAccountActivitySparklines(): Promise<
  AccountActivitySparklineDto[]
> {
  try {
    return await apiClient<AccountActivitySparklineDto[]>(
      "/api/accounts/activity-sparklines",
      { method: "GET", cache: "no-store" }
    );
  } catch (error) {
    logError(error, "fetchAccountActivitySparklines");
    throw error;
  }
}

/**
 * Sends content to the AI parse endpoint to extract activity fields.
 * @param data - Autofill payload with activity content and customer account context.
 * @returns A promise that resolves with the extracted fields (title, date, participants, summary, etc).
 * @throws An error if the request fails.
 */
export async function activityAutofill(
  data: ActivityAutofillRequest
): Promise<ActivityAutofill> {
  try {
    return await apiClient<ActivityAutofill>("/api/activities/autofill", {
      method: "POST",
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "activityAutofill");
    throw error;
  }
}

/**
 * Deletes an activity by its ID.
 *
 * @param activityId - The ID of the activity to delete
 * @returns A promise that resolves when the activity is deleted
 */
export async function deleteActivity(activityId: string): Promise<void> {
  try {
    const response = await fetch(`/api/activities/${activityId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete activity");
    }
    return await response.json();
  } catch (error) {
    logger.error("Error deleting activity", error, {
      context: { source: "service", module: "activitiesServices" },
    });
    throw error;
  }
}

/**
 * Fetches a single activity by its ID.
 *
 * @param activityId - The ID of the activity to fetch
 * @returns The activity with its audit fields
 * @throws An ApiError carrying the response status when the request fails
 */
export async function fetchActivityById(
  activityId: string
): Promise<ActivityWithAuditDto> {
  try {
    return await apiClient<ActivityWithAuditDto>(
      `/api/activities/${activityId}`,
      { method: "GET", cache: "no-store" }
    );
  } catch (error) {
    logError(error, "fetchActivityById");
    throw error;
  }
}
